import { cancelJoinRequest } from "./teams";
import { supabase } from "./supabase";
import { daysRemainingUntilClose } from "./studentCases";

export type StudentTeamRow = {
  team_id: string;
  team_name: string;
  competition_id: string;
  competition_name: string;
  competition_status: "draft" | "open" | "judging" | "closed";
  competition_closes_at: string | null;
  member_role: "captain" | "member";
  member_count: number;
  team_size_max: number;
  members: Array<{
    student_id: string;
    display_name: string;
    avatar_url: string | null;
  }>;
  /** Past teams only: `competition_results` has a row for this team. */
  has_competition_results: boolean;
};

export type PendingJoinRequest = {
  request_id: string;
  team_id: string;
  team_name: string;
  competition_name: string;
  requested_at: string;
};

type TeamRow = {
  id: string;
  name: string;
  owner_id: string;
  competition_id: string;
};

type CompRow = {
  id: string;
  name: string;
  status: string;
  closes_at: string | null;
  max_team_size: number | null;
};

function normalizeCompetitionStatus(status: string): StudentTeamRow["competition_status"] {
  if (status === "open" || status === "judging" || status === "closed" || status === "draft") return status;
  return "draft";
}

function isActiveBucketStatus(status: StudentTeamRow["competition_status"]): boolean {
  return status === "open" || status === "judging";
}

function isPastBucketStatus(status: StudentTeamRow["competition_status"]): boolean {
  return status === "closed";
}

function buildMemberList(
  team: TeamRow,
  memberStudentIds: string[],
  profileById: Map<string, { full_name: string | null; avatar_url: string | null }>
): StudentTeamRow["members"] {
  const ids = [...new Set([team.owner_id, ...memberStudentIds].filter(Boolean))];
  const rows = ids.map((student_id) => {
    const p = profileById.get(student_id);
    const display_name = (p?.full_name ?? "").trim() || "Member";
    return {
      student_id,
      display_name,
      avatar_url: p?.avatar_url ?? null,
    };
  });
  rows.sort((a, b) => {
    if (a.student_id === team.owner_id) return -1;
    if (b.student_id === team.owner_id) return 1;
    return a.display_name.localeCompare(b.display_name);
  });
  return rows;
}

/**
 * Teams the student is on: **active** = competition `open` or `judging`; **past** = `closed`.
 * Teams in `draft` (or any other status) are omitted.
 */
/** Sidebar badge: teams the student is on in competitions that are `open` or `judging`. */
export async function countActiveTeamsForStudent(studentId: string): Promise<number> {
  const { data: mems } = await supabase.from("team_members").select("team_id").eq("student_id", studentId);
  if (!mems?.length) return 0;
  const teamIds = [...new Set(mems.map((m) => m.team_id).filter(Boolean))] as string[];
  if (teamIds.length === 0) return 0;
  const { data: teams } = await supabase.from("teams").select("competition_id").in("id", teamIds);
  if (!teams?.length) return 0;
  const compIds = [...new Set(teams.map((t) => t.competition_id).filter(Boolean))] as string[];
  if (compIds.length === 0) return 0;
  const { data: comps } = await supabase.from("competitions").select("id, status").in("id", compIds);
  const activeCompIds = new Set(
    (comps ?? [])
      .filter((c) => c.status === "open" || c.status === "judging")
      .map((c) => c.id as string)
  );
  let n = 0;
  for (const t of teams) {
    if (t.competition_id && activeCompIds.has(t.competition_id as string)) n++;
  }
  return n;
}

// Stale-while-revalidate cache keyed by student id. Filled on every
// successful listMyTeams call; the page reads via getCachedTeams() for
// instant first paint while a fresh fetch runs in the background. 5min
// TTL — long enough that quick tab-hopping is instant, short enough that
// you don't see hopelessly stale rosters after a real change.
const TEAMS_CACHE_TTL_MS = 5 * 60 * 1000;
type TeamsCacheEntry = {
  active: StudentTeamRow[];
  past: StudentTeamRow[];
  cachedAt: number;
};
const teamsCache = new Map<string, TeamsCacheEntry>();

/** Synchronous read of the in-memory cache. Returns null if missing or expired. */
export function getCachedTeams(
  studentId: string
): { active: StudentTeamRow[]; past: StudentTeamRow[] } | null {
  const entry = teamsCache.get(studentId);
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > TEAMS_CACHE_TTL_MS) {
    teamsCache.delete(studentId);
    return null;
  }
  return { active: entry.active, past: entry.past };
}

export async function listMyTeams(studentId: string): Promise<{
  active: StudentTeamRow[];
  past: StudentTeamRow[];
}> {
  // Phase 1: get team memberships (single round-trip — every later phase
  // needs the team IDs).
  const { data: myRows, error: memErr } = await supabase
    .from("team_members")
    .select("team_id")
    .eq("student_id", studentId);
  if (memErr || !myRows?.length) {
    const empty = { active: [], past: [] };
    teamsCache.set(studentId, { ...empty, cachedAt: Date.now() });
    return empty;
  }

  const teamIds = [...new Set(myRows.map((r) => r.team_id).filter(Boolean))] as string[];
  if (teamIds.length === 0) {
    const empty = { active: [], past: [] };
    teamsCache.set(studentId, { ...empty, cachedAt: Date.now() });
    return empty;
  }

  // Phase 2: teams + every team's full member list + competition_results
  // all run in parallel. competition_results gets fetched for every team
  // (not just past) so we don't need a second round-trip after we know
  // which teams are closed — a small bandwidth tax for one fewer hop.
  const [teamsRes, allMembersRes, resultsRes] = await Promise.all([
    supabase.from("teams").select("id, name, owner_id, competition_id").in("id", teamIds),
    supabase.from("team_members").select("team_id, student_id").in("team_id", teamIds),
    supabase.from("competition_results").select("team_id").in("team_id", teamIds),
  ]);

  const teamsData = teamsRes.data;
  if (teamsRes.error || !teamsData?.length) {
    const empty = { active: [], past: [] };
    teamsCache.set(studentId, { ...empty, cachedAt: Date.now() });
    return empty;
  }
  const teams = teamsData as TeamRow[];

  const membersByTeam = new Map<string, string[]>();
  for (const m of allMembersRes.data ?? []) {
    if (!m.team_id || !m.student_id) continue;
    const arr = membersByTeam.get(m.team_id) ?? [];
    arr.push(m.student_id as string);
    membersByTeam.set(m.team_id, arr);
  }

  const teamsWithResults = new Set(
    (resultsRes.data ?? []).map((r) => r.team_id as string).filter(Boolean)
  );

  // Phase 3: competitions + user_profiles run in parallel. Both depend on
  // phase 2 (which comp IDs are present, which student profiles are in
  // any team roster) but are independent of each other.
  const compIds = [...new Set(teams.map((t) => t.competition_id).filter(Boolean))] as string[];

  const allStudentIds = new Set<string>();
  for (const t of teams) {
    allStudentIds.add(t.owner_id);
    for (const sid of membersByTeam.get(t.id) ?? []) allStudentIds.add(sid);
  }
  const profileIds = [...allStudentIds];

  const [compsRes, profilesRes] = await Promise.all([
    compIds.length > 0
      ? supabase
          .from("competitions")
          .select("id, name, status, closes_at, max_team_size")
          .in("id", compIds)
      : Promise.resolve({ data: [] as CompRow[], error: null }),
    profileIds.length > 0
      ? supabase.from("user_profiles").select("id, full_name, avatar_url").in("id", profileIds)
      : Promise.resolve({ data: [] as { id: string; full_name: string | null; avatar_url: string | null }[], error: null }),
  ]);

  if (compsRes.error || !compsRes.data?.length) {
    const empty = { active: [], past: [] };
    teamsCache.set(studentId, { ...empty, cachedAt: Date.now() });
    return empty;
  }

  const compById = new Map<string, CompRow>();
  for (const c of compsRes.data as CompRow[]) {
    compById.set(c.id, c);
  }

  const profileById = new Map<string, { full_name: string | null; avatar_url: string | null }>();
  for (const p of profilesRes.data ?? []) {
    profileById.set(p.id as string, {
      full_name: (p.full_name as string | null) ?? null,
      avatar_url: (p.avatar_url as string | null) ?? null,
    });
  }

  // Phase 4: stitch + sort. Pure synchronous transformation — no network.
  const active: StudentTeamRow[] = [];
  const past: StudentTeamRow[] = [];

  for (const team of teams) {
    const comp = compById.get(team.competition_id);
    if (!comp) continue;

    const st = normalizeCompetitionStatus(comp.status);
    if (!isActiveBucketStatus(st) && !isPastBucketStatus(st)) continue;

    const memberIds = membersByTeam.get(team.id) ?? [];
    const idSet = new Set<string>(memberIds);
    idSet.add(team.owner_id);
    const member_count = idSet.size;
    const team_size_max =
      typeof comp.max_team_size === "number" && !Number.isNaN(comp.max_team_size) ? comp.max_team_size : 5;

    const members = buildMemberList(team, memberIds, profileById);
    const member_role: "captain" | "member" = team.owner_id === studentId ? "captain" : "member";

    const row: StudentTeamRow = {
      team_id: team.id,
      team_name: team.name,
      competition_id: comp.id,
      competition_name: comp.name,
      competition_status: st,
      competition_closes_at: comp.closes_at,
      member_role,
      member_count,
      team_size_max,
      members,
      has_competition_results: isPastBucketStatus(st) && teamsWithResults.has(team.id),
    };

    if (isActiveBucketStatus(st)) active.push(row);
    else past.push(row);
  }

  const closeKey = (r: StudentTeamRow) => (r.competition_closes_at ? new Date(r.competition_closes_at).getTime() : Number.POSITIVE_INFINITY);
  active.sort((a, b) => closeKey(a) - closeKey(b) || a.competition_name.localeCompare(b.competition_name));
  past.sort((a, b) => closeKey(b) - closeKey(a) || a.competition_name.localeCompare(b.competition_name));

  teamsCache.set(studentId, { active, past, cachedAt: Date.now() });
  return { active, past };
}

export async function listMyPendingJoinRequests(studentId: string): Promise<PendingJoinRequest[]> {
  const { data: reqs, error } = await supabase
    .from("team_join_requests")
    .select("id, team_id, created_at, status")
    .eq("student_id", studentId)
    .eq("status", "pending");

  if (error || !reqs?.length) return [];

  const teamIds = [...new Set(reqs.map((r) => r.team_id).filter(Boolean))] as string[];
  if (teamIds.length === 0) return [];

  const { data: teamsData } = await supabase.from("teams").select("id, name, competition_id").in("id", teamIds);
  const teamById = new Map<string, { name: string; competition_id: string }>();
  for (const t of teamsData ?? []) {
    teamById.set(t.id as string, { name: String(t.name ?? ""), competition_id: t.competition_id as string });
  }

  const compIds = [...new Set([...teamById.values()].map((v) => v.competition_id).filter(Boolean))] as string[];
  const { data: comps } = await supabase.from("competitions").select("id, name").in("id", compIds);
  const compNameById = new Map<string, string>();
  for (const c of comps ?? []) {
    compNameById.set(c.id as string, String(c.name ?? ""));
  }

  const out: PendingJoinRequest[] = [];
  for (const r of reqs) {
    const tid = r.team_id as string;
    const t = teamById.get(tid);
    if (!t) continue;
    const created = (r as { created_at?: string }).created_at;
    out.push({
      request_id: r.id as string,
      team_id: tid,
      team_name: t.name || "Team",
      competition_name: compNameById.get(t.competition_id) ?? "Competition",
      requested_at: (typeof created === "string" && created ? created : new Date().toISOString()),
    });
  }

  out.sort((a, b) => new Date(b.requested_at).getTime() - new Date(a.requested_at).getTime());
  return out;
}

/** Student withdraws their own pending join request (sets status to `cancelled`). */
export async function withdrawJoinRequest(requestId: string): Promise<{ error: Error | null }> {
  return cancelJoinRequest(requestId);
}

export function teamDaysRemainingLabel(closesAt: string | null): string | null {
  const daysLeft = daysRemainingUntilClose(closesAt);
  if (daysLeft == null) return null;
  if (daysLeft <= 0) return "Closed";
  return `${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining`;
}
